/**
 * useMedicalConsultation Hook
 * State management for medical consultation module
 *
 * @version 2025.1.0
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import {
  MedicalRecord,
  ConsultationQueueItem,
  ConsultationQueueFilters,
  MedicalRecordFilters,
  StartConsultationData,
  UpdateMedicalRecordData,
  CompleteConsultationData,
  MedicalRecordStatistics,
  EmployeeMedicalHistory,
} from '../types/medicalRecord';
import medicalRecordService from '../services/medicalRecordService';
import { getApiErrorMessage } from '../types/common';
import { getSocket } from '../services/socket';

// ============================================================================
// INTERFACES
// ============================================================================

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface UseMedicalConsultationOptions {
  initialFilters?: ConsultationQueueFilters;
  autoLoad?: boolean;
  enableRealtime?: boolean; // Socket.IO real-time (default: true)
}

interface UseMedicalConsultationReturn {
  // State
  queue: ConsultationQueueItem[];
  loading: boolean;
  filters: ConsultationQueueFilters;
  pagination: Pagination;
  statistics: MedicalRecordStatistics | null;
  statisticsLoading: boolean;
  socketConnected: boolean;

  // Active consultation state
  activeRecord: MedicalRecord | null;
  activeRecordLoading: boolean;

  // Filter actions
  setFilters: (filters: ConsultationQueueFilters) => void;
  updateFilter: <K extends keyof ConsultationQueueFilters>(
    key: K,
    value: ConsultationQueueFilters[K]
  ) => void;
  clearFilters: () => void;

  // Pagination actions
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;

  // Data actions
  loadQueue: () => Promise<void>;
  loadStatistics: () => Promise<void>;
  refresh: () => Promise<void>;

  // Consultation flow actions
  startConsultation: (data: StartConsultationData) => Promise<MedicalRecord | null>;
  updateRecord: (id: number, data: UpdateMedicalRecordData) => Promise<MedicalRecord | null>;
  completeConsultation: (id: number, data: CompleteConsultationData) => Promise<MedicalRecord | null>;
  generateAso: (id: number) => Promise<{ pdfPath: string } | null>;
  generateProntuario: (id: number) => Promise<{ pdfPath: string } | null>;

  // Active record actions
  loadActiveRecord: (id: number) => Promise<void>;
  loadRecordByAppointment: (appointmentId: number) => Promise<MedicalRecord | null>;
  clearActiveRecord: () => void;

  // Employee history
  loadEmployeeHistory: (employeeId: number) => Promise<EmployeeMedicalHistory | null>;
}

// ============================================================================
// DEFAULTS
// ============================================================================

const defaultFilters: ConsultationQueueFilters = {
  date: new Date().toISOString().split('T')[0], // Today
  attendanceStatus: ['CHECKED_IN', 'TRIAGED'], // Ready for consultation (check-in done or triaged)
};

const defaultPagination: Pagination = {
  page: 1,
  limit: 50,
  total: 0,
  totalPages: 0,
};

// ============================================================================
// HOOK
// ============================================================================

export function useMedicalConsultation(
  options: UseMedicalConsultationOptions = {}
): UseMedicalConsultationReturn {
  const { initialFilters = defaultFilters, autoLoad = true, enableRealtime = true } = options;
  const { enqueueSnackbar } = useSnackbar();

  // Queue state
  const [queue, setQueue] = useState<ConsultationQueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFiltersState] = useState<ConsultationQueueFilters>(initialFilters);
  const [pagination, setPagination] = useState<Pagination>(defaultPagination);

  // Statistics state
  const [statistics, setStatistics] = useState<MedicalRecordStatistics | null>(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);

  // Socket.IO state
  const [socketConnected, setSocketConnected] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Active record state
  const [activeRecord, setActiveRecord] = useState<MedicalRecord | null>(null);
  const [activeRecordLoading, setActiveRecordLoading] = useState(false);

  // =========================================================================
  // LOAD DATA
  // =========================================================================

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const result = await medicalRecordService.getConsultationQueue({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });

      setQueue(result.data);
      setPagination((prev) => ({
        ...prev,
        total: result.pagination.total,
        totalPages: result.pagination.totalPages,
      }));
    } catch (error) {
      console.error('Error loading consultation queue:', error);
      enqueueSnackbar('Erro ao carregar fila de consultas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit, enqueueSnackbar]);

  const loadStatistics = useCallback(async () => {
    setStatisticsLoading(true);
    try {
      const statsFilters: MedicalRecordFilters = {};
      if (filters.companyId) statsFilters.companyId = filters.companyId;
      if (filters.clinicId) statsFilters.clinicId = filters.clinicId;
      if (filters.date) {
        statsFilters.startDate = filters.date;
        statsFilters.endDate = filters.date;
      }

      const result = await medicalRecordService.getStatistics(statsFilters);
      setStatistics(result);
    } catch (error) {
      console.error('Error loading statistics:', error);
      // Don't show error for statistics, it's not critical
    } finally {
      setStatisticsLoading(false);
    }
  }, [filters.companyId, filters.clinicId, filters.date]);

  const refresh = useCallback(async () => {
    await Promise.all([loadQueue(), loadStatistics()]);
  }, [loadQueue, loadStatistics]);

  // =========================================================================
  // FILTER ACTIONS
  // =========================================================================

  const setFilters = useCallback((newFilters: ConsultationQueueFilters) => {
    setFiltersState(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const updateFilter = useCallback(
    <K extends keyof ConsultationQueueFilters>(key: K, value: ConsultationQueueFilters[K]) => {
      setFiltersState((prev) => ({ ...prev, [key]: value }));
      setPagination((prev) => ({ ...prev, page: 1 }));
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFiltersState(defaultFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // =========================================================================
  // PAGINATION ACTIONS
  // =========================================================================

  const setPage = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  // =========================================================================
  // CONSULTATION FLOW ACTIONS
  // =========================================================================

  const startConsultation = useCallback(
    async (data: StartConsultationData): Promise<MedicalRecord | null> => {
      try {
        const record = await medicalRecordService.startConsultation(data);
        setActiveRecord(record);
        enqueueSnackbar('Consulta iniciada com sucesso', { variant: 'success' });

        // Refresh queue to update status
        loadQueue();
        loadStatistics();

        return record;
      } catch (error) {
        console.error('Error starting consultation:', error);
        const errorMessage = getApiErrorMessage(error, 'Erro ao iniciar consulta');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        return null;
      }
    },
    [enqueueSnackbar, loadQueue, loadStatistics]
  );

  const updateRecord = useCallback(
    async (id: number, data: UpdateMedicalRecordData): Promise<MedicalRecord | null> => {
      try {
        const record = await medicalRecordService.updateMedicalRecord(id, data);
        setActiveRecord(record);
        // Don't show success message for auto-save updates
        return record;
      } catch (error) {
        console.error('Error updating medical record:', error);
        const errorMessage = getApiErrorMessage(error, 'Erro ao salvar registro');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        return null;
      }
    },
    [enqueueSnackbar]
  );

  const completeConsultation = useCallback(
    async (id: number, data: CompleteConsultationData): Promise<MedicalRecord | null> => {
      try {
        const record = await medicalRecordService.completeConsultation(id, data);
        setActiveRecord(record);
        enqueueSnackbar('Consulta concluída com sucesso', { variant: 'success' });

        // Refresh queue and statistics
        loadQueue();
        loadStatistics();

        return record;
      } catch (error) {
        console.error('Error completing consultation:', error);
        const errorMessage = getApiErrorMessage(error, 'Erro ao concluir consulta');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        return null;
      }
    },
    [enqueueSnackbar, loadQueue, loadStatistics]
  );

  const generateAso = useCallback(
    async (id: number): Promise<{ pdfPath: string } | null> => {
      try {
        const result = await medicalRecordService.generateAsoPdf(id);
        enqueueSnackbar('ASO gerado com sucesso', { variant: 'success' });
        return result;
      } catch (error) {
        console.error('Error generating ASO:', error);
        const errorMessage = getApiErrorMessage(error, 'Erro ao gerar ASO');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        return null;
      }
    },
    [enqueueSnackbar]
  );

  const generateProntuario = useCallback(
    async (id: number): Promise<{ pdfPath: string } | null> => {
      try {
        const result = await medicalRecordService.generateProntuarioPdf(id);
        enqueueSnackbar('Prontuário gerado com sucesso', { variant: 'success' });
        return result;
      } catch (error) {
        console.error('Error generating Prontuário:', error);
        const errorMessage = getApiErrorMessage(error, 'Erro ao gerar Prontuário');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        return null;
      }
    },
    [enqueueSnackbar]
  );

  // =========================================================================
  // ACTIVE RECORD ACTIONS
  // =========================================================================

  const loadActiveRecord = useCallback(
    async (id: number) => {
      setActiveRecordLoading(true);
      try {
        const record = await medicalRecordService.getMedicalRecord(id);
        setActiveRecord(record);
      } catch (error) {
        console.error('Error loading medical record:', error);
        enqueueSnackbar('Erro ao carregar registro médico', { variant: 'error' });
      } finally {
        setActiveRecordLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  const loadRecordByAppointment = useCallback(
    async (appointmentId: number): Promise<MedicalRecord | null> => {
      setActiveRecordLoading(true);
      try {
        const record = await medicalRecordService.getMedicalRecordByAppointment(appointmentId);
        if (record) {
          setActiveRecord(record);
        }
        return record;
      } catch (error) {
        console.error('Error loading medical record by appointment:', error);
        return null;
      } finally {
        setActiveRecordLoading(false);
      }
    },
    []
  );

  const clearActiveRecord = useCallback(() => {
    setActiveRecord(null);
  }, []);

  // =========================================================================
  // EMPLOYEE HISTORY
  // =========================================================================

  const loadEmployeeHistory = useCallback(
    async (employeeId: number): Promise<EmployeeMedicalHistory | null> => {
      try {
        return await medicalRecordService.getEmployeeHistory(employeeId);
      } catch (error) {
        console.error('Error loading employee history:', error);
        enqueueSnackbar('Erro ao carregar histórico do funcionário', { variant: 'error' });
        return null;
      }
    },
    [enqueueSnackbar]
  );

  // =========================================================================
  // EFFECTS
  // =========================================================================

  // Auto-load on mount and filter/pagination change
  useEffect(() => {
    if (autoLoad) {
      loadQueue();
    }
  }, [loadQueue, autoLoad]);

  // Load statistics when filters change
  useEffect(() => {
    if (autoLoad) {
      loadStatistics();
    }
  }, [loadStatistics, autoLoad]);

  // Socket.IO real-time updates
  useEffect(() => {
    if (!enableRealtime) return;

    const socket = getSocket();

    const onConnect = () => {
      socket.emit('join:consultations');
      setSocketConnected(true);
    };

    const onDisconnect = () => {
      setSocketConnected(false);
    };

    const onConsultationsUpdated = (payload: { clinicId: number; date?: string }) => {
      const clinicMatches = !filters.clinicId || payload.clinicId === filters.clinicId;
      const dateMatches = !filters.date || !payload.date || payload.date === filters.date;

      if (clinicMatches && dateMatches) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          loadQueue();
        }, 300);
      }
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('consultations:updated', onConsultationsUpdated);

    if (socket.connected) {
      socket.emit('join:consultations');
      setSocketConnected(true);
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('consultations:updated', onConsultationsUpdated);
      socket.emit('leave:consultations');
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [enableRealtime, filters.clinicId, filters.date, loadQueue]);

  // Adaptive polling fallback: 60s when connected, 10s when disconnected
  useEffect(() => {
    if (!enableRealtime) return;

    const interval = socketConnected ? 60000 : 10000;
    const timer = setInterval(() => {
      loadQueue();
    }, interval);

    return () => clearInterval(timer);
  }, [enableRealtime, socketConnected, loadQueue]);

  // =========================================================================
  // RETURN
  // =========================================================================

  return {
    // State
    queue,
    loading,
    filters,
    pagination,
    statistics,
    statisticsLoading,
    socketConnected,

    // Active consultation state
    activeRecord,
    activeRecordLoading,

    // Filter actions
    setFilters,
    updateFilter,
    clearFilters,

    // Pagination actions
    setPage,
    setLimit,

    // Data actions
    loadQueue,
    loadStatistics,
    refresh,

    // Consultation flow actions
    startConsultation,
    updateRecord,
    completeConsultation,
    generateAso,
    generateProntuario,

    // Active record actions
    loadActiveRecord,
    loadRecordByAppointment,
    clearActiveRecord,

    // Employee history
    loadEmployeeHistory,
  };
}

export default useMedicalConsultation;
